import { createPortal } from 'preact/compat';
import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { useRemembered } from '../lib/remember';
import { useNotice } from '../shell/notice';
import { useFlag, useMatches } from '../shell/hooks';
import { Chip, ChipRow, FilterChip, InterestDots } from '../ui/bits';
import { ListRow, ListTable, type Col } from '../ui/ListTable';
import { Sheet } from '../book/reading';
import { INTEREST_HINTS, interestHint } from '../book/changes';
import { bookLabel, todayIn } from '../book/model';
import { loadDetails, type Details, type Shelves } from '../shelves/load';
import { buildGroups, filterCount, matches, pruneSubgenres, recentFrom, type FilterFacts, type FilterLists, type FilterState } from '../shelves/filters';
import { FilterBar, FilterPanel } from '../shelves/Filters';
import { BookSheet, type BookSheetKind } from './BookSheet';
import { BulkBar, InterestMenu, MoveMenu, NARROW_INTEREST, RowBook, RowCheck, SelectShown, TypeMenu, flipSort } from './parts';
import { SettingMenu } from '../ui/PillMenu';
import { ListFoot } from '../ui/ListFoot';
import { setBook, tryAgainBook } from './actions';
import { PAGE, booksText, copyText, rowsOf, tabDef, type QueueRow } from './model';
import { REVISIT_KINDS, REVISIT_SORTS, kindCounts, kindDef, revisitRows, sortRevisit, whyText, type RevisitFacts, type RevisitKind, type RevisitSort, type RevisitSortKey } from './revisit';
import { loadRevisitFacts } from './revisitLoad';

// The Queue › Revisit (Phase 6.3 Part 2, agreed with Kristi Oct 30; mocked up in chat, laptop and
// phone): ↺ Try again, ↻ Read again, Abandoned and Passed on in one list. Filter chips narrow it to
// a kind (remembered; none on is every kind); Abandoned and Passed on rows are greyed. Each row has its status's shortcut into the
// shared sheets (Start again · Start a re-read · Try again · Bring back), shown over 1,000 px only
// (Kristi, Oct 30), and the shared Move ▾, which on an Abandoned book also offers Try again.
// Sorted by status, newest first within each, by default; every sort breaks ties by newest.

type Row = QueueRow & { kind: RevisitKind };

export function RevisitList({
  wide,
  shelves,
  facts,
  readTypes,
  slots,
  top,
}: {
  wide: boolean;
  shelves: Shelves;
  facts: { facts: FilterFacts[]; lists: FilterLists } | null;
  readTypes: string[];
  /** The pinned bar's places for Filters, Sort and Select several, and the Filters panel. */
  slots: { tools: HTMLElement | null; panel: HTMLElement | null };
  top?: ComponentChildren;
}) {
  const notice = useNotice();
  const today = todayIn();
  const { path } = useRoute();
  const narrowInterest = useMatches(NARROW_INTEREST);
  const editOn = useFlag('book_page.edit');
  // The kinds shown (R3 P5, Kristi Nov 2): none on is every kind. A new key: the old one listed hidden kinds.
  const [kinds, setKinds] = useRemembered<RevisitKind[]>('queue:revisit:kinds', []);
  const [sort, setSort] = useRemembered<RevisitSort>('queue:sort:revisit', { key: 'status', reversed: false });
  const [filters, setFilters] = useRemembered<FilterState>('queue:filters:revisit', {});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  const [why, setWhy] = useState<Map<string, RevisitFacts>>(new Map());
  const [bookSheet, setBookSheet] = useState<{ row: Row; what: BookSheetKind } | null>(null);
  const [tryAgain, setTryAgain] = useState<Row | null>(null);

  const factsById = useMemo(() => new Map(facts?.facts.map((f) => [f.id, f]) ?? []), [facts]);
  const from = facts ? recentFrom(today, facts.lists.recentDays) : today;
  const base = useMemo(() => revisitRows(shelves), [shelves]);
  const all: Row[] = useMemo(() => {
    const kinds = new Map(base.map((b) => [b.id, b.kind]));
    return rowsOf(base, factsById).map((r) => ({ ...r, kind: kinds.get(r.id)! }));
  }, [base, factsById]);

  // The Why column's reads, for every book in Revisit (a few dozen at most for most people).
  const ids = all.map((b) => b.id).join(',');
  useEffect(() => {
    if (!all.length) return;
    let live = true;
    loadRevisitFacts(all.map((b) => b.id))
      .then((m) => live && setWhy(m))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [ids]);

  // The panel's chips count within the Kinds picked too (filters review F10, Kristi Nov 2).
  const tabFacts = useMemo(() => {
    const set = new Set(all.filter((b) => !kinds.length || kinds.includes(b.kind)).map((b) => b.id));
    return facts?.facts.filter((f) => set.has(f.id)) ?? [];
  }, [facts, all, kinds]);
  const groups = useMemo(() => (facts ? buildGroups(tabFacts, facts.lists, filters, from).filter((g) => g.key !== 'shelf') : []), [facts, tabFacts, filters, from]);
  const rows = useMemo(() => {
    const on = filterCount(filters) > 0;
    const kept = all.filter((b) => (!kinds.length || kinds.includes(b.kind)) && (!on || (factsById.get(b.id) && matches(factsById.get(b.id)!, filters, from))));
    return sortRevisit(kept, sort, why, copyText);
  }, [all, kinds, filters, sort, why, factsById, from]);
  const narrowed = filterCount(filters) > 0 || kinds.length > 0;
  // Each Show chip counts within what the Filters panel leaves (R7 N3, Kristi Nov 2: answer 4a).
  const counts = useMemo(() => {
    const on = filterCount(filters) > 0;
    return kindCounts(on ? all.filter((b) => factsById.get(b.id) && matches(factsById.get(b.id)!, filters, from)) : all);
  }, [all, filters, factsById, from]);

  const visible = rows.slice(0, shown);
  useEffect(() => {
    const missing = visible.filter((b) => !details.has(b.id));
    if (!missing.length) return;
    let live = true;
    loadDetails(missing, new Set())
      .then((m) => live && setDetails((old) => new Map([...old, ...m])))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [visible.map((b) => b.id).join(',')]);

  const open = (b: Row) => () => navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide });
  const shortcut = (b: Row) => {
    if (b.kind === 'try_again' || b.kind === 'read_again') setBookSheet({ row: b, what: { kind: 'start' } });
    else if (b.kind === 'abandoned') setTryAgain(b);
    else setBookSheet({ row: b, what: { kind: 'place', to: 'future_reads' } });
  };
  const toggleKind = (k: RevisitKind) => {
    setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]);
    setShown(PAGE);
  };
  const sortBy = (k: RevisitSortKey) => {
    setSort(flipSort(sort, k));
    setShown(PAGE);
  };

  // Select several (as on the other tabs): Move, Read type, Interest; one Undo.
  const isSel = (id: string) => selected.includes(id);
  const toggleSel = (id: string) => setSelected(isSel(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const shownIds = visible.map((b) => b.id);
  // From 700 to 1,000 px ★ and Copy fold under the author and the shortcut goes (Move ▾ does it).
  const fold = wide && narrowInterest;
  const cols: Col<RevisitSortKey>[] = [
    ...(selecting ? [{ label: <SelectShown shownIds={shownIds} selected={selected} setSelected={setSelected} />, width: '26px' }] : []),
    { label: 'Status', width: fold ? '100px' : '104px', sort: 'status' as RevisitSortKey },
    { label: 'Book', width: fold ? 'minmax(0, 1.6fr)' : 'minmax(0, 1.5fr)', sort: 'title' as RevisitSortKey, book: true },
    { label: 'Read type', width: '128px', sort: 'readtype' as RevisitSortKey },
    { label: 'Why', width: fold ? 'minmax(0, 1.4fr)' : 'minmax(0, 1.5fr)', sort: 'newest' as RevisitSortKey, title: 'Newest set aside' },
    { label: 'Interest', width: 'var(--q-interest-w)', sort: 'interest' as RevisitSortKey },
    ...(fold
      ? []
      : [
          { label: '★', width: '56px', sort: 'goodreads' as RevisitSortKey, num: true, title: 'Goodreads' },
          { label: 'Copy', width: '92px', sort: 'copy' as RevisitSortKey },
        ]),
    { width: fold ? '84px' : '196px' },
  ];
  const setType = (b: Row, name: string | null) => void setBook(notice, b, 'read_type', name, name ? `${b.title}: ${name}` : `Read type cleared for ${b.title}`);
  const setInterest = (b: Row, v: number | null) => void setBook(notice, b, 'interest', v, v === null ? `Interest cleared for ${b.title}` : `Interest set to ${interestHint(v)}`);

  const tools = (
    <>
      <span class="shelves-divider" aria-hidden="true" />
      <FilterBar
        groups={groups}
        state={filters}
        open={filtersOpen}
        onToggle={() => setFiltersOpen(!filtersOpen)}
        onChange={(s) => {
          setFilters(facts ? pruneSubgenres(s, facts.lists) : s);
          setShown(PAGE);
        }}
        showing={narrowed ? rows.length : null}
      />
      <span class="queue-top-end">
        <SettingMenu<RevisitSortKey>
          name="Sort"
          choices={REVISIT_SORTS}
          value={sort.key}
          onChange={(k) => {
            setSort({ key: k, reversed: false });
            setShown(PAGE);
          }}
        />
        <button
          type="button"
          class={`btn-secondary queue-select${selecting ? ' on' : ''}`}
          aria-pressed={selecting}
          aria-label="Select several"
          onClick={() => {
            setSelecting(!selecting);
            setSelected([]);
          }}
        >
          {wide ? 'Select several' : 'Select'}
        </button>
        {top}
      </span>
    </>
  );
  const panel = filtersOpen ? (
        facts ? (
          <FilterPanel groups={groups} state={filters} onChange={(s) => setFilters(pruneSubgenres(s, facts.lists))} onClose={() => setFiltersOpen(false)} showing={rows.length} />
        ) : (
          <p class="shelves-quiet">Loading the filters…</p>
        )
      ) : null;
  return (
    <>
      <div class="tab-head">
        <p class="queue-description">{tabDef('revisit').description}</p>
        {/* No counts on the Queue's tabs (R7 N1): the tab's count is its grey line here (N2). */}
        <p class="mb-needs quiet">{booksText(all.length)}</p>
      </div>
      {slots.tools ? createPortal(tools, slots.tools) : <div class="rv-tools">{tools}</div>}
      {slots.panel ? (panel ? createPortal(panel, slots.panel) : null) : panel}
      {/* Filter chips (R3 P2, P5; Kristi Nov 2, answer 2a): none on is every kind; tap Abandoned to
          see only those, again for all. Each counts within what Filters leaves; a kind at 0 hides. */}
      <div class="rv-controls">
        <ChipRow label="Kinds" scroll={!wide}>
          {REVISIT_KINDS.map((k) => (
            <FilterChip key={k.key} label={k.label} count={counts[k.key]} on={kinds.includes(k.key)} onToggle={() => toggleKind(k.key)} />
          ))}
        </ChipRow>
      </div>
      <ListTable<RevisitSortKey>
        label="Revisit"
        class={`rv-list${selecting ? ' selecting' : ''}`}
        cols={cols}
        sort={{ key: sort.key, desc: sort.reversed }}
        onSort={sortBy}
        empty={all.length ? 'Nothing here matches.' : 'Nothing to revisit yet: books you set aside, mark ↻ Read again or pass on come here.'}
        onClear={
          all.length
            ? () => {
                setFilters({});
                setKinds([]);
                setShown(PAGE);
              }
            : undefined
        }
        foot={<ListFoot shown={Math.min(shown, rows.length)} total={rows.length} page={PAGE} onMore={() => setShown(shown + PAGE)} onAll={() => setShown(rows.length)} onFewer={() => setShown(PAGE)} />}
      >
        {visible.map((raw) => {
          const b = { ...raw, ...details.get(raw.id), owned: raw.owned } as Row;
          const def = kindDef(b.kind);
          const copy = copyText(b);
          const gr = b.goodreads ? `★ ${b.goodreads.toFixed(2)}` : null;
          const check = selecting ? <RowCheck title={b.title} on={isSel(b.id)} onToggle={() => toggleSel(b.id)} /> : null;
          const status = (
            <span class="rv-status">
              <span class={`pill${def.greyed ? ' quiet' : ''}`}>{def.label}</span>
            </span>
          );
          // The phone, and an upright iPad, fold ★ and Copy into the line under the author.
          const book = <RowBook title={b.title} sub={[b.author, b.series].filter(Boolean).join(' · ')} cover={b.cover} meta={fold || !wide ? [gr, copy ?? 'Not owned yet'].filter(Boolean).join(' · ') : null} onOpen={open(b)} />;
          const type = (
            <span class="q-type">
              <TypeMenu row={b} names={readTypes} onPick={(n) => setType(b, n)} />
            </span>
          );
          const whyCell = <span class="rv-why">{why.has(b.id) ? whyText(b, why.get(b.id), today) : ''}</span>;
          const actions = (
            <span class="rv-actions">
              {/* The shortcut is for the widest screens (Kristi, Oct 30); narrower ones use Move ▾ alone. */}
              {wide && !fold ? (
                <button type="button" class="btn-book rv-shortcut" onClick={() => shortcut(b)}>
                  {def.shortcut}
                </button>
              ) : null}
              {/* An Abandoned book can be tried again: back to Future reads with ↺ on, one Undo (Kristi, Oct 30). */}
              <MoveMenu
                row={b}
                pastRead={Boolean(editOn)}
                onSheet={(what) => setBookSheet({ row: b, what })}
                extra={b.kind === 'abandoned' ? { label: 'Future reads, with ↺ Try again…', note: 'asks where', run: () => setTryAgain(b) } : undefined}
              />
            </span>
          );
          return (
            <ListRow
              key={b.id}
              data-row={b.id}
              class={`rv-row${def.greyed ? ' rv-grey' : ''}`}
              selected={isSel(b.id)}
              quiet={def.greyed}
              cells={[
                ...(selecting ? [check] : []),
                status,
                book,
                type,
                whyCell,
                <span class="q-interest">
                  {narrowInterest ? <InterestMenu row={b} onPick={(v) => setInterest(b, v)} /> : <InterestDots value={b.interest} hints={INTEREST_HINTS} onChange={(v) => setInterest(b, v)} />}
                </span>,
                ...(fold
                  ? []
                  : [
                      <span class="q-gr" title="Goodreads">
                        {gr ?? '–'}
                      </span>,
                      <span class="q-copy">{copy ? copy : <span class="tile-notowned">Not owned yet</span>}</span>,
                    ]),
                actions,
              ]}
              phone={{
                lead: check,
                book,
                control: actions,
                line: (
                  <>
                    {whyCell}
                    <span class="q-controls">
                      {status}
                      {type}
                      <span class="q-interest">
                        <InterestMenu row={b} onPick={(v) => setInterest(b, v)} />
                      </span>
                    </span>
                  </>
                ),
              }}
            />
          );
        })}
      </ListTable>
      {selecting ? <BulkBar rows={rows} selected={selected} setSelected={setSelected} readTypes={readTypes} /> : null}
      {bookSheet ? <BookSheet label={bookSheet.row.label} title={bookSheet.row.title} what={bookSheet.what} onClose={() => setBookSheet(null)} /> : null}
      {tryAgain ? (
        <TryAgainSheet
          title={tryAgain.title}
          onClose={() => setTryAgain(null)}
          onMove={async (place) => {
            const b = tryAgain;
            setTryAgain(null);
            await tryAgainBook(notice, b, place);
          }}
        />
      ) : null}
    </>
  );
}

/** Try again on an Abandoned book: back to Future reads with ↺ on; where on the shelf, as any move there. */
function TryAgainSheet({ title, onClose, onMove }: { title: string; onClose: () => void; onMove: (place: 'kept' | 'top' | 'bottom') => void }) {
  const [place, setPlace] = useState<'kept' | 'top' | 'bottom'>('kept');
  const options: ['kept' | 'top' | 'bottom', string][] = [
    ['kept', 'Its old place, or the bottom'],
    ['top', 'Top'],
    ['bottom', 'Bottom'],
  ];
  return (
    <Sheet title={`Try ${title} again?`} sub="It goes back to Future reads with ↺ Try again on. One Undo takes back both." onClose={onClose}>
      <fieldset class="bk-place">
        <legend class="bk-label">Where on Future reads</legend>
        <div class="bk-dialog-chips">
          {options.map(([k, label]) => (
            <Chip key={k} label={label} on={place === k} onToggle={() => setPlace(k)} />
          ))}
        </div>
      </fieldset>
      <div class="bk-buttons">
        <button type="button" class="btn-main" onClick={() => onMove(place)}>
          Try again
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}
